import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, startOfMonth } from "date-fns";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarClock,
  Loader2,
  RefreshCw,
  Wallet,
  Building2,
  Stethoscope,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  expensesApi,
  type PendingExpenseRow,
  type PendingPaymentUrgency,
  type PendingPaymentsReport,
} from "@/api/expenses";
import { useAuth } from "@/contexts/AuthContext";
import { useChainReportHospitalScope } from "@/hooks/useChainReportHospitalScope";
import { ReportHospitalFilter } from "@/components/reports/ReportHospitalFilter";
import { formatDate } from "@/utils/dateUtils";

const PAYMENT_MODES = ["CASH", "CARD", "UPI", "BANK_TRANSFER", "CHEQUE", "OTHER"] as const;

function formatCurrency(n: number) {
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function categoryLabel(cat: string) {
  return cat.replace(/_/g, " ");
}

function urgencyBadge(urgency: PendingPaymentUrgency) {
  if (urgency === "CRITICAL") {
    return (
      <Badge variant="outline" className="text-red-800 border-red-600 bg-red-50">
        {urgency}
      </Badge>
    );
  }
  if (urgency === "HIGH") {
    return (
      <Badge variant="outline" className="text-amber-900 border-amber-600 bg-amber-50">
        {urgency}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-slate-700 border-slate-400 bg-slate-50">
      {urgency}
    </Badge>
  );
}

function ageLabel(days: number) {
  if (days <= 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function expensesBasePath(role?: string) {
  if (role === "SUPER_ADMIN") return "/super-admin/expenses";
  if (role === "RECEPTIONIST" || role === "OPERATIONS_MANAGER") return "/receptionist/expenses";
  return "/hospital-admin/expenses";
}

export default function PendingPaymentsReportPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isSuperAdmin,
  } = useChainReportHospitalScope({ defaultScope: "consolidated" });
  const [from, setFrom] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [tab, setTab] = useState<"ipd" | "central" | "alerts">("ipd");

  const [markOpen, setMarkOpen] = useState(false);
  const [markTarget, setMarkTarget] = useState<PendingExpenseRow | null>(null);
  const [markMode, setMarkMode] = useState<string>("BANK_TRANSFER");
  const [markAmount, setMarkAmount] = useState("");

  const params = useMemo(() => {
    const p: { hospitalId?: string; from?: string; to?: string } = {};
    if (showHospitalFilter) {
      if (!reportHospitalId && isSuperAdmin) return null;
      if (reportHospitalId) p.hospitalId = reportHospitalId;
    }
    if (from) p.from = from;
    if (to) p.to = `${to}T23:59:59.999`;
    return p;
  }, [showHospitalFilter, reportHospitalId, isSuperAdmin, from, to]);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ["pending-payments-report", params],
    queryFn: async () => {
      const res = await expensesApi.reportPendingPayments(params!);
      return res.data?.data as PendingPaymentsReport;
    },
    enabled: !!params,
    staleTime: 30_000,
  });

  const markMut = useMutation({
    mutationFn: async () => {
      if (!markTarget) throw new Error("No row selected");
      const raw = markAmount.trim();
      const paidAmount = raw ? Number(raw) : undefined;
      if (raw && (!Number.isFinite(paidAmount) || (paidAmount as number) <= 0)) {
        throw new Error("Enter a valid paid amount");
      }
      const body = {
        paymentMode: markMode,
        ...(paidAmount != null ? { paidAmount } : {}),
      };
      if (markTarget.kind === "patient") {
        return expensesApi.markPatientPaid(markTarget.id, body);
      }
      return expensesApi.markCentralPaid(markTarget.id, body);
    },
    onSuccess: () => {
      toast.success("Marked paid");
      setMarkOpen(false);
      setMarkTarget(null);
      void qc.invalidateQueries({ queryKey: ["pending-payments-report"] });
      void qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(ax.response?.data?.message || ax.message || "Failed to mark paid");
    },
  });

  const summary = data?.summary;
  const expensesPath = expensesBasePath(user?.role);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pending Payments</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Unpaid IPD patient and Central expenses with pendency age, plus proactive alerts when
            OPD consultations by visiting doctors (not RMO / not salaried) or discharged IPD
            surgeries have no matching payout expense entry.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to={expensesPath}>Open Expenses</Link>
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-4 items-end">
          {showHospitalFilter ? (
            <ReportHospitalFilter
              hospitals={hospitalOptions}
              value={selectedHospitalId}
              onChange={setSelectedHospitalId}
              isSuperAdmin={isSuperAdmin}
            />
          ) : null}
          <div className="space-y-1">
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground pb-2">
            Filters unpaid expense dates. Alerts use the same window (default: this month; empty
            range on API defaults to last 90 days for gaps).
          </p>
        </CardContent>
      </Card>

      {showHospitalFilter && isSuperAdmin && !reportHospitalId ? (
        <p className="text-sm text-muted-foreground">Select a hospital to load the report.</p>
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading pending payments…
        </div>
      ) : isError ? (
        <p className="text-sm text-red-700">
          {(error as Error)?.message || "Could not load pending payments report"}
        </p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-amber-700" />
                  IPD patient unpaid
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-amber-800">
                  {formatCurrency(summary?.ipdAmount ?? 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.ipdCount ?? 0} expense{(summary?.ipdCount ?? 0) === 1 ? "" : "s"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-sky-700" />
                  Central unpaid
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-sky-800">
                  {formatCurrency(summary?.centralAmount ?? 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.centralCount ?? 0} expense{(summary?.centralCount ?? 0) === 1 ? "" : "s"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-700" />
                  Missing expense entries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold text-rose-800">
                  {(summary?.missingOpdCount ?? 0) + (summary?.missingIpdSurgeonCount ?? 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.missingOpdCount ?? 0} OPD · {summary?.missingIpdSurgeonCount ?? 0} IPD
                  surgeon
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-slate-700" />
                  Oldest pendency
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-semibold">{ageLabel(summary?.oldestAgeDays ?? 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">Across unpaid + alerts</p>
              </CardContent>
            </Card>
          </div>

          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="ipd" className="gap-1.5">
                IPD Patient
                <Badge variant="secondary">{data?.ipd.length ?? 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="central" className="gap-1.5">
                Central
                <Badge variant="secondary">{data?.central.length ?? 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="alerts" className="gap-1.5">
                Attention needed
                <Badge variant="secondary">
                  {(data?.alerts.missingOpdPayouts.length ?? 0) +
                    (data?.alerts.missingIpdSurgeonPayouts.length ?? 0)}
                </Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="ipd" className="mt-4">
              <ExpenseTable
                rows={data?.ipd ?? []}
                empty="No unpaid IPD patient expenses in this range."
                showPatient
                onMarkPaid={(row) => {
                  setMarkTarget(row);
                  setMarkMode("BANK_TRANSFER");
                  setMarkAmount(String(row.balance));
                  setMarkOpen(true);
                }}
              />
            </TabsContent>

            <TabsContent value="central" className="mt-4">
              <ExpenseTable
                rows={data?.central ?? []}
                empty="No unpaid central expenses in this range."
                showUnit
                onMarkPaid={(row) => {
                  setMarkTarget(row);
                  setMarkMode("BANK_TRANSFER");
                  setMarkAmount(String(row.balance));
                  setMarkOpen(true);
                }}
              />
            </TabsContent>

            <TabsContent value="alerts" className="mt-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Stethoscope className="h-4 w-4" />
                    Missing OPD payout expense (doctor, not RMO)
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    OPD consultation bills exist for a non-salaried doctor, but no Central{" "}
                    <strong>OPD_PAYOUT</strong> expense was entered for that doctor in the same
                    service month. Create the expense under Expenses → Central, then mark paid
                    here once settled.
                  </p>
                </CardHeader>
                <CardContent>
                  {(data?.alerts.missingOpdPayouts.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">No missing OPD payout gaps found.</p>
                  ) : (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Urgency</TableHead>
                            <TableHead>Age</TableHead>
                            <TableHead>Doctor</TableHead>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">OPDs</TableHead>
                            <TableHead className="text-right">Consult revenue</TableHead>
                            <TableHead>First → last</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data!.alerts.missingOpdPayouts.map((row) => (
                            <TableRow key={row.key} className="bg-rose-50/40">
                              <TableCell>{urgencyBadge(row.urgency)}</TableCell>
                              <TableCell className="font-medium whitespace-nowrap">
                                {ageLabel(row.ageDays)}
                              </TableCell>
                              <TableCell className="font-medium">{row.doctorName}</TableCell>
                              <TableCell>{row.serviceMonth}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {row.consultationCount}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {formatCurrency(row.consultationRevenue)}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDate(row.firstBillDate)} → {formatDate(row.lastBillDate)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button asChild size="sm" variant="outline">
                                  <Link to={expensesPath}>Create OPD payout</Link>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Missing IPD surgeon payout expense
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Discharged IPD stays with surgery where no surgeon / assistant / visiting
                    consultant patient expense exists. Enter under Expenses → Patient (IPD).
                  </p>
                </CardHeader>
                <CardContent>
                  {(data?.alerts.missingIpdSurgeonPayouts.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No missing IPD surgeon payout gaps found.
                    </p>
                  ) : (
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Urgency</TableHead>
                            <TableHead>Age</TableHead>
                            <TableHead>Patient</TableHead>
                            <TableHead>IPD #</TableHead>
                            <TableHead>Surgery</TableHead>
                            <TableHead>Doctor</TableHead>
                            <TableHead>Discharged</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data!.alerts.missingIpdSurgeonPayouts.map((row) => (
                            <TableRow key={row.admissionId} className="bg-amber-50/50">
                              <TableCell>{urgencyBadge(row.urgency)}</TableCell>
                              <TableCell className="font-medium whitespace-nowrap">
                                {ageLabel(row.ageDays)}
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">{row.patientName ?? "—"}</div>
                                <div className="text-xs text-muted-foreground">
                                  {row.patientUhid ?? ""}
                                </div>
                              </TableCell>
                              <TableCell>{row.ipdNumber ?? "—"}</TableCell>
                              <TableCell className="max-w-[180px] truncate">
                                {row.surgeryName ?? "—"}
                              </TableCell>
                              <TableCell>{row.assignedDoctorName ?? "—"}</TableCell>
                              <TableCell className="whitespace-nowrap">
                                {formatDate(row.dischargeDate)}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button asChild size="sm" variant="outline">
                                  <Link to={expensesPath}>Add IPD expense</Link>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      <Dialog open={markOpen} onOpenChange={(o) => !o && setMarkOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark payment as paid</DialogTitle>
          </DialogHeader>
          {markTarget && (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1">
                <p>
                  <span className="text-muted-foreground">Category:</span>{" "}
                  <strong>{categoryLabel(markTarget.category)}</strong>
                </p>
                <p>
                  <span className="text-muted-foreground">Balance:</span>{" "}
                  <strong className="text-amber-800">{formatCurrency(markTarget.balance)}</strong>
                </p>
                <p>
                  <span className="text-muted-foreground">Age:</span>{" "}
                  {ageLabel(markTarget.ageDays)} ({urgencyBadge(markTarget.urgency)})
                </p>
              </div>
              <div>
                <Label>Payment mode</Label>
                <Select value={markMode} onValueChange={setMarkMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Paid amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={markAmount}
                  onChange={(e) => setMarkAmount(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Defaults to full balance. Partial amounts mark as partially paid.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => markMut.mutate()} disabled={markMut.isPending || !markTarget}>
              {markMut.isPending ? "Saving…" : "Confirm paid"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpenseTable({
  rows,
  empty,
  showPatient,
  showUnit,
  onMarkPaid,
}: {
  rows: PendingExpenseRow[];
  empty: string;
  showPatient?: boolean;
  showUnit?: boolean;
  onMarkPaid: (row: PendingExpenseRow) => void;
}) {
  const total = Math.round(rows.reduce((s, r) => s + r.balance, 0) * 100) / 100;
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{empty}</p>;
  }
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Urgency</TableHead>
                <TableHead>Age</TableHead>
                <TableHead>Category</TableHead>
                {showPatient && <TableHead>Patient / IPD</TableHead>}
                {showUnit && <TableHead>Unit</TableHead>}
                <TableHead>Payee</TableHead>
                <TableHead>Expense date</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.kind}-${row.id}`}>
                  <TableCell>{urgencyBadge(row.urgency)}</TableCell>
                  <TableCell className="font-medium whitespace-nowrap">
                    {ageLabel(row.ageDays)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{categoryLabel(row.category)}</div>
                    {row.subCategory ? (
                      <div className="text-xs text-muted-foreground">{row.subCategory}</div>
                    ) : null}
                  </TableCell>
                  {showPatient && (
                    <TableCell>
                      <div className="font-medium">{row.patientName ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {[row.ipdNumber, row.patientUhid].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </TableCell>
                  )}
                  {showUnit && <TableCell>{row.hospitalUnit ?? "—"}</TableCell>}
                  <TableCell>{row.payeeName ?? "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(row.expenseDate)}
                    {row.serviceMonth ? (
                      <div className="text-xs text-muted-foreground">Svc {row.serviceMonth}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-amber-800 tabular-nums">
                    {formatCurrency(row.balance)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{row.paymentStatus}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => onMarkPaid(row)}>
                      Mark paid
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={showPatient || showUnit ? 7 : 6} className="font-semibold">
                  Total ({rows.length})
                </TableCell>
                <TableCell className="text-right font-semibold text-amber-800">
                  {formatCurrency(total)}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
